import type { Edge, Node } from '@xyflow/react';
import { MarkerType } from '@xyflow/react';
import { diagramEdges, diagramNodes, stages } from '../data/graph';
import type { DiagramEdgeContent, DiagramNodeContent, FunnelFilter, Language } from '../types';

export interface StageNodeData extends Record<string, unknown> {
  kind: 'stage';
  stageId: string;
  title: string;
  shortTitle: string;
  number?: string;
  tone: string;
}

export interface SystemNodeData extends Record<string, unknown> {
  kind: 'system';
  content: DiagramNodeContent;
  title: string;
  active: boolean;
  selected: boolean;
  connected: boolean;
  dimmed: boolean;
  roleLens: boolean;
  funnelMatch: boolean;
  language: Language;
}

export interface DataEdgeData extends Record<string, unknown> {
  content: DiagramEdgeContent;
  label: string;
  active: boolean;
  emphasized: boolean;
  dimmed: boolean;
  playing: boolean;
  reducedMotion: boolean;
  funnelMatch: boolean;
}

export type PfdNode = Node<StageNodeData | SystemNodeData>;
export type PfdEdge = Edge<DataEdgeData>;

export const buildNodes = (
  language: Language,
  activeNodeIds: Set<string>,
  selectedNodeId: string | null,
  connectedNodeIds: Set<string>,
  hoveredNodeId: string | null,
  roleLens: boolean,
  focusPath: boolean,
  funnelFilter: FunnelFilter,
): PfdNode[] => {
  const stageNodes: PfdNode[] = stages.map((stage) => ({
    id: `stage-${stage.id}`,
    type: 'stageLane',
    position: { x: stage.x, y: stage.y },
    data: { kind: 'stage', stageId: stage.id, title: stage.title[language], shortTitle: stage.shortTitle[language], number: stage.number, tone: stage.tone },
    style: { width: stage.width, height: stage.height, zIndex: -2 },
    draggable: false,
    selectable: false,
    focusable: false,
  }));

  const systemNodes: PfdNode[] = [];
  for (const stage of stages) {
    const contents = diagramNodes.filter((node) => node.stageId === stage.id);
    contents.forEach((content, index) => {
      const governance = stage.id === 'governance';
      const hubOffset = contents.slice(0, index).filter((node) => node.variant === 'hub').length * 28;
      const available = stage.height - (governance ? 70 : 118);
      const step = governance ? (stage.width - 60) / contents.length : available / Math.max(contents.length, 1);
      const active = activeNodeIds.has(content.id);
      const selected = selectedNodeId === content.id;
      const connected = connectedNodeIds.has(content.id) || hoveredNodeId === content.id;
      const hasFocus = activeNodeIds.size > 0 || selectedNodeId !== null || hoveredNodeId !== null || focusPath;
      const inFocusedPath = selected || connected || active;
      const funnelMatch = funnelFilter === 'all' || content.funnelStages.includes(funnelFilter);
      systemNodes.push({
        id: content.id,
        type: 'systemNode',
        parentId: `stage-${stage.id}`,
        extent: 'parent',
        position: governance
          ? { x: 28 + index * step, y: 83 }
          : { x: 20, y: 76 + index * step + hubOffset },
        data: {
          kind: 'system', content, title: content.title[language], active, selected, connected,
          dimmed: hasFocus && !inFocusedPath, roleLens, language, funnelMatch,
        },
        style: {
          width: governance ? Math.max(242, step - 16) : stage.width - 40,
          height: content.variant === 'hub' ? 72 : governance ? 56 : 42,
          zIndex: content.variant === 'hub' ? 4 : 2,
        },
        draggable: false,
        selectable: true,
      });
    });
  }
  return [...stageNodes, ...systemNodes];
};

export const buildEdges = (
  language: Language,
  activeEdgeIds: Set<string>,
  emphasizedEdgeIds: Set<string>,
  hasFocus: boolean,
  playing: boolean,
  reducedMotion: boolean,
  funnelFilter: FunnelFilter,
): PfdEdge[] => diagramEdges.map((content) => {
  const active = activeEdgeIds.has(content.id);
  const emphasized = emphasizedEdgeIds.has(content.id);
  const dimmed = hasFocus && !active && !emphasized;
  const funnelMatch = funnelFilter === 'all' || content.funnelStages.includes(funnelFilter);
  const sourceStage = stages.find((stage) => stage.id === diagramNodes.find((node) => node.id === content.source)?.stageId);
  const targetStage = stages.find((stage) => stage.id === diagramNodes.find((node) => node.id === content.target)?.stageId);
  const verticalDirection = sourceStage && targetStage ? Math.sign(targetStage.y - sourceStage.y) : 0;
  const sourceHandle = verticalDirection === 0 ? 'right-source' : 'bottom-source';
  const targetHandle = verticalDirection === 0 ? 'left-target' : 'top-target';
  return {
    id: content.id,
    source: content.source,
    target: content.target,
    type: 'dataEdge',
    data: { content, label: content.label[language], active, emphasized, dimmed, playing, reducedMotion, funnelMatch },
    sourceHandle,
    targetHandle,
    markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12, color: 'currentColor' },
    markerStart: content.flowType === 'bidirectional' ? { type: MarkerType.ArrowClosed, width: 12, height: 12, color: 'currentColor', orient: 'auto-start-reverse' } : undefined,
    zIndex: active ? 5 : content.flowType === 'control' ? 0 : 1,
    selectable: false,
    focusable: false,
  };
});

export const getConnected = (nodeId: string | null): { nodes: Set<string>; edges: Set<string> } => {
  if (!nodeId) return { nodes: new Set(), edges: new Set() };
  const nodes = new Set([nodeId]);
  const edges = new Set<string>();
  diagramEdges.forEach((edge) => {
    if (edge.source === nodeId || edge.target === nodeId) {
      nodes.add(edge.source);
      nodes.add(edge.target);
      edges.add(edge.id);
    }
  });
  return { nodes, edges };
};

export const searchNodes = (query: string, language: Language): DiagramNodeContent[] => {
  const normalized = query.trim().toLocaleLowerCase(language === 'az' ? 'az-AZ' : 'en-US');
  if (!normalized) return [];
  return diagramNodes.filter((node) => [
    node.title.en, node.title.az, node.summary.en, node.summary.az, node.purpose.en, node.purpose.az,
    ...node.incomingData, ...node.outgoingData, ...node.kpis, ...node.owners, ...(node.searchTerms ?? []), ...(node.exampleVendors ?? []),
  ].join(' ').toLocaleLowerCase(language === 'az' ? 'az-AZ' : 'en-US').includes(normalized));
};
